<template>
  <div id="lead-tracker">
    <div class="panel">
      <div class="panel-head">
        <h2>Lead Tracker</h2>
      </div>
      <div class="panel-content">
        <div class="status-message">
          <div class="message">{{statusMessage}}</div>
          <a @click.prevent="showNoteEdit = true" href="#" class="note-update">update</a>
        </div>

        <div id="settings">
          <div class="row">
            <div class="col col-2x">
              <form>
                <div class="form-control-group">
                  <div class="form-control">
                    <label for="startDate">Start Date</label>
                    <datepicker
                      id="startDate"
                      :format="formatDate"
                      :value="startDate"
                      @input="setStartDate($event)"
                      required
                    ></datepicker>
                  </div>
                  <div class="form-control">
                    <label for="endDate">End Date</label>
                    <datepicker
                      id="endDate"
                      :format="formatDate"
                      :value="endDate"
                      @input="setEndDate($event)"
                      required
                    ></datepicker>
                  </div>
                  <div class="form-control">
                    <button @click.prevent="matchDate()" class="match button button-blue">Match Date</button>
                  </div>
                </div>

                <div class="button-row">
                  <button
                    class="button button-small button-blue"
                    @click.prevent="twoDaysAgo()"
                  >2 Days Ago</button>
                  <button
                    class="button button-small button-blue"
                    @click.prevent="thisWeek()"
                  >This Week</button>
                  <button
                    class="button button-small button-blue"
                    @click.prevent="thisMonth()"
                  >This Month</button>
                  <button
                    class="button button-small button-blue"
                    @click.prevent="twoMonthsAgo()"
                  >2 Months Ago</button>
                  <button
                    class="button button-small button-blue"
                    @click.prevent="thisYear()"
                  >This Year</button>
                </div>

                <div class="button-row">
                  <button
                    class="button button-small button-blue"
                    @click.prevent="threeDaysAgo()"
                  >3 Days Ago</button>
                  <button
                    class="button button-small button-blue"
                    @click.prevent="lastWeek()"
                  >Last Week</button>
                  <button
                    class="button button-small button-blue"
                    @click.prevent="lastMonth()"
                  >Last Month</button>
                  <button
                    class="button button-small button-blue"
                    @click.prevent="threeMonthsAgo()"
                  >3 Months Ago</button>
                  <button
                    class="button button-small button-blue"
                    @click.prevent="lastYear()"
                  >Last Year</button>
                </div>

                <div class="form-control">
                  <label>
                    <input
                      type="checkbox"
                      name="management"
                      :value="management"
                      @change="setManagement($event.target.checked)"
                    /> Include Management Fee
                  </label>
                </div>

                <button @click.prevent="updateData()" class="button button-orange">Generate Report</button>
                <fieldset>
                  <legend>Include in CSV export</legend>
                  <label for="totals">
                    <input id="totals" type="checkbox" value="totals" v-model="csv.include" checked />Totals
                  </label>
                  <label for="google">
                    <input id="google" type="checkbox" value="google" v-model="csv.include" checked />Google
                  </label>
                  <label for="microsoft">
                    <input id="microsoft" type="checkbox" value="microsoft" v-model="csv.include" />Microsoft
                  </label>
                  <label for="facebook">
                    <input id="facebook" type="checkbox" value="facebook" v-model="csv.include" />Facebook
                  </label>
                </fieldset>
                <button @click.prevent="exportToCSV()" class="button button-blue">Export to CSV</button>
              </form>
            </div>

            <div class="col">
              <span>Select Clients</span>
              <ul class="client-selection">
                <li v-for="list in clientLists" :key="list.id">
                  <label>
                    <input
                      type="checkbox"
                      :value="list.id"
                      @change="updateListIds({ add: $event.target.checked, listId: list.id})"
                      :checked="listIds.indexOf(list.id) > -1"
                    />
                    <strong>List:</strong>
                    {{ list.name }}
                  </label>
                </li>
                <li v-for="client in clients" :key="client.id">
                  <label>
                    <input
                      type="checkbox"
                      :value="client.id"
                      @change="updateClientIds({ add: $event.target.checked, clientId: client.id})"
                      :checked="clientIds.indexOf(client.id) > -1"
                    />
                    {{ client.name }}
                  </label>
                </li>
              </ul>
              <router-link
                class="client-list-edit"
                :to="{ name: 'ListIndex' }"
              >add / remove client lists</router-link>
            </div>
          </div>
        </div>

        <loading v-if="status.loading"></loading>
        <div v-show="noReportData || reportData.length == 0" class="no-data-message">
          <p>
            <em>No data available matching these parameters.</em>
          </p>
        </div>
        <div v-if="reportData.length > 0" id="results">
          <div class="controls">
            <a @click.prevent="goToTotal()" href="#" class="btn btn--goto">Go To Totals</a>
            <a @click.prevent="expandAll()" href="#">expand all </a>
            <span>/</span>
            <a @click.prevent="collapseAll()" href="#"> collapse all</a>
          </div>
          <table id="lead-tracking-table">
            <thead class="header">
              <tr>
                <th @click.prevent="sortColumn()">Client</th>
                <th @click.prevent="sortColumn()">Calls</th>
                <th @click.prevent="sortColumn()">Call Leads</th>
                <th @click.prevent="sortColumn()">Call Lead %</th>
                <th @click.prevent="sortColumn()">Form Leads</th>
                <th @click.prevent="sortColumn()">Total Leads</th>
                <th @click.prevent="sortColumn()">Total Spent</th>
                <th @click.prevent="sortColumn()">Cost / Lead</th>
                <th>Chart</th>
                <th>Data</th>
                <th @click.prevent="sortColumn()">3 Mo Avg CPL</th>
              </tr>
            </thead>
            <template v-for="(data,i) in reportData">
              <tbody>
                <tr>
                  <td>
                    <a @click.prevent="data.showAll = !data.showAll" href="#">
                      <strong>{{ data.client.name }}</strong>
                    </a>
                  </td>
                  <td>{{ data.click_to_call ? data.click_to_call.calls : 0 }}</td>
                  <td>{{ data.click_to_call ? data.click_to_call.conversions : 0 }}</td>
                  <td>{{ data.click_to_call && !isNaN(Math.round(data.click_to_call.conversions / data.click_to_call.calls * 100)) ? Math.round(data.click_to_call.conversions / data.click_to_call.calls * 100) : 0 }}%</td>
                  <td>{{ getFormLeads(data) }}</td>
                  <td>{{ getTotalLeads(data) }}</td>
                  <td>${{ getTotalSpent(data).toLocaleString('en', numberFormat) }}</td>
                  <td>${{ getTotalCostPerLead(data).toLocaleString('en', numberFormat) }}</td>
                  <td class="align-center">
                    <router-link :to="{ name: 'ClientReview', query: { clientId: data.client.id }}">
                      <i class="fa fa-line-chart"></i>
                    </router-link>
                  </td>
                  <td class="align-center">
                    <router-link :to="{ name: 'DataManager', query: { clientId: data.client.id }}">
                      <i class="fa fa-table"></i>
                    </router-link>
                  </td>
                  <td>${{ data.monthly ? getTotalCostPerLead(data.monthly).toLocaleString('en', numberFormat) : '0.00' }}</td>
                </tr>
                <tr class="breakout" v-if="data.showAll && data.google">
                  <td class="medium">
                    <strong>Google</strong>
                  </td>
                  <td></td>
                  <td>{{ data.google.calls.toLocaleString('en', numberFormat) }}</td>
                  <td></td>
                  <td>{{ data.google.conversions }}</td>
                  <td></td>
                  <td>{{ getSpent(data, 'google').toLocaleString('en', numberFormat) }}</td>
                  <td>${{ getCostPerLead(data, 'google').toLocaleString('en', numberFormat) }}</td>
                  <td></td>
                  <td></td>
                  <td>${{ (data.monthly && data.monthly.google) ? getCostPerLead(data.monthly, 'google').toLocaleString('en', numberFormat) : '0.00' }}</td>
                </tr>
                <tr class="breakout" v-if="data.showAll && data.microsoft">
                  <td class="medium">
                    <strong>Microsoft</strong>
                  </td>
                  <td></td>
                  <td>{{ data.microsoft.calls.toLocaleString('en', numberFormat) }}</td>
                  <td></td>
                  <td>{{ data.microsoft.conversions }}</td>
                  <td></td>
                  <td>{{ getSpent(data, 'microsoft').toLocaleString('en', numberFormat) }}</td>
                  <td>${{ getCostPerLead(data, 'microsoft').toLocaleString('en', numberFormat) }}</td>
                  <td></td>
                  <td></td>
                  <td>${{ (data.monthly && data.monthly.microsoft) ? getCostPerLead(data.monthly, 'microsoft').toLocaleString('en', numberFormat) : '0.00' }}</td>
                </tr>
                <tr class="breakout" v-if="data.showAll && data.facebook" :key="i">
                  <td class="medium">
                    <strong>Facebook</strong>
                  </td>
                  <td></td>
                  <td>{{ data.facebook.calls.toLocaleString('en', numberFormat) }}</td>
                  <td></td>
                  <td>{{ data.facebook.conversions }}</td>
                  <td></td>
                  <td>{{ getSpent(data, 'facebook').toLocaleString('en', numberFormat) }}</td>
                  <td>${{ getCostPerLead(data, 'facebook').toLocaleString('en', numberFormat) }}</td>
                  <td></td>
                  <td></td>
                  <td>${{ (data.monthly && data.monthly.facebook) ? getCostPerLead(data.monthly, 'facebook').toLocaleString('en', numberFormat) : '0.00' }}</td>
                </tr>
              </tbody>
            </template>
            <tbody>
              <tr>
                <td>
                  <a @click.prevent="showAllTotals = !showAllTotals" href="#">
                    <strong>Totals</strong>
                  </a>
                </td>
                <td>{{totalCalls.toLocaleString()}}</td>
                <td>{{totalCallLeads.toLocaleString()}}</td>
                <td>{{ !isNaN(Math.round(totalCallLeads/totalCalls*100)) ? Math.round(totalCallLeads/totalCalls*100) : 0 }}%</td>
                <td>{{totalFormLeads.toLocaleString()}}</td>
                <td>{{totalLeads.toLocaleString()}}</td>
                <td>${{totalSpend.toLocaleString('en', numberFormat)}}</td>
                <td>${{totalSpend/totalLeads | USD}}</td>
                <td></td>
                <td></td>
                <td>${{totalMonthlyAvg.toLocaleString('en', numberFormat)}}</td>

              </tr>
              <tr class="breakout" v-if="showAllTotals">
                <td class="medium">
                  <strong>Google</strong>
                </td>
                <td></td>
                <td>{{totalGoogleCallLeads}}</td>
                <td></td>
                <td>{{totalGoogleLeads}}</td>
                <td></td>
                <td>${{totalGoogleSpend | round}}</td>
                <td>${{ getTotalChannelCostPerLead(totalGoogleSpend, totalGoogleLeads + totalGoogleCallLeads) | round }}</td>
                <td></td>
                <td></td>
                <td>${{totalGoogleMonthlyAvg.toLocaleString('en', numberFormat)}}</td>
              </tr>
              <tr class="breakout" v-if="showAllTotals">
                <td class="medium">
                  <strong>Microsoft</strong>
                </td>
                <td></td>
                <td>{{totalMicrosoftCallLeads}}</td>
                <td></td>
                <td>{{totalMicrosoftLeads}}</td>
                <td></td>
                <td>${{totalMicrosoftSpend | round}}</td>
                <td>${{ getTotalChannelCostPerLead(totalMicrosoftSpend, totalMicrosoftLeads + totalMicrosoftCallLeads) | round }}</td>
                <td></td>
                <td></td>
                <td>${{totalMicrosoftMonthlyAvg.toLocaleString('en', numberFormat)}}</td>
              </tr>
              <tr class="breakout" v-if="showAllTotals">
                <td class="medium">
                  <strong>Facebook</strong>
                </td>
                <td></td>
                <td>{{totalFacebookCallLeads}}</td>
                <td></td>
                <td>{{totalFacebookLeads}}</td>
                <td></td>
                <td>${{totalFacebookSpend | round}}</td>
                <td>${{ getTotalChannelCostPerLead(totalFacebookSpend, totalFacebookLeads + totalFacebookCallLeads) | round }}</td>
                <td></td>
                <td></td>
                <td>${{totalFacebookMonthlyAvg.toLocaleString('en', numberFormat)}}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <note-edit
      :show="showNoteEdit"
      noteId="2"
      @update="getStatusMessage()"
      @close="showNoteEdit = false"
    ></note-edit>
  </div>
</template>

<script>
import NoteEdit from "@/components/ui/NoteEdit";
import Loading from "@/components/ui/Loading";
import Datepicker from "vuejs-datepicker";
import moment from "moment";

import { mapActions, mapGetters } from "vuex";

export default {
  name: "LeadTracker",
  components: {
    Loading,
    Datepicker,
    NoteEdit
  },
  data() {
    return {
      statusMessage: "Checking For latest status...",
      clientLists: [],
      clients: [],
      reportData: [],
      noReportData: false,
      showAllTotals: false,
      showNoteEdit: false,
      numberFormat: {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      stickyHeader: false,
      sorted: {
        col: 0,
        status: true
      },
      status: {
        loading: false
      },
      csv: {
        include: ["totals", "google", "microsoft", "facebook"]
      }
    };
  },
  created() {
    this.getClients();
    this.getLists();
    this.getStatusMessage();
    window.addEventListener("scroll", this.onScroll);
    if (this.startDate == null && this.endDate == null) {
      this.thisMonth();
    }
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
  },
  mounted() {
    if (
      this.startDate &&
      this.endDate &&
      (this.clientIds.length > 0 || this.listIds.length > 0)
    )
      this.updateData();
  },
  computed: {
    //These are just input fields
    ...mapGetters({
      startDate: "getStartDate",
      endDate: "getEndDate",
      listIds: "getListIds",
      clientIds: "getClientIds",
      management: "getManagement",
      input: "getInput"
    }),
    totalCalls() {
      return this.reportData
        .filter(row => row.click_to_call)
        .map(row => row.click_to_call)
        .reduce((sum, row) => {
          return sum + parseInt(row.calls);
        }, 0);
    },
    totalCallLeads() {
      return this.reportData
        .filter(row => row.click_to_call)
        .map(row => row.click_to_call)
        .reduce((sum, row) => {
          return sum + parseInt(row.conversions);
        }, 0);
    },

    totalFacebookLeads() {
      return this.reportData
        .filter(row => row.facebook)
        .map(row => row.facebook)
        .reduce((sum, row) => {
          return sum + parseInt(row.conversions);
        }, 0);
    },
    totalGoogleLeads() {
      return this.reportData
        .filter(row => row.google)
        .map(row => row.google)
        .reduce((sum, row) => {
          return sum + parseInt(row.conversions);
        }, 0);
    },
    totalMicrosoftLeads() {
      return this.reportData
        .filter(row => row.microsoft)
        .map(row => row.microsoft)
        .reduce((sum, row) => {
          return sum + parseInt(row.conversions);
        }, 0);
    },

    totalFacebookCallLeads() {
      return this.reportData
        .filter(row => row.facebook)
        .map(row => row.facebook)
        .reduce((sum, row) => {
          return sum + parseInt(row.calls);
        }, 0);
    },
    totalGoogleCallLeads() {
      return this.reportData
        .filter(row => row.google)
        .map(row => row.google)
        .reduce((sum, row) => {
          return sum + parseInt(row.calls);
        }, 0);
    },
    totalMicrosoftCallLeads() {
      return this.reportData
        .filter(row => row.microsoft)
        .map(row => row.microsoft)
        .reduce((sum, row) => {
          return sum + parseInt(row.calls);
        }, 0);
    },

    totalFormLeads() {
      return (
        this.totalFacebookLeads +
        this.totalGoogleLeads +
        this.totalMicrosoftLeads
      );
    },
    totalLeads() {
      return this.totalFormLeads + this.totalCallLeads;
    },

    totalGoogleMonthlyAvg() {
      let spend = [];
      for(let i = 0; i < this.reportData.length; i++) {
        if (this.reportData[i].monthly &&
          this.reportData[i].monthly.google &&
          this.reportData[i].monthly.google.conversions > 0) {
          spend.push(this.getCostPerLead(this.reportData[i].monthly, 'google'));
        }
      }
      if(spend.length > 0) {
        let sum = spend.reduce((previous, current) => current += previous);
        let avg = sum / spend.length;
        return avg;
      }
      return 0;
    },
    totalMicrosoftMonthlyAvg() {
      let spend = [];
      for(let i = 0; i < this.reportData.length; i++) {
        if (this.reportData[i].monthly &&
          this.reportData[i].monthly.microsoft &&
          this.reportData[i].monthly.microsoft.conversions > 0) {
          spend.push(this.getCostPerLead(this.reportData[i].monthly, 'microsoft'));
        }
      }
      if(spend.length > 0) {
        let sum = spend.reduce((previous, current) => current += previous);
        let avg = sum / spend.length;
        return avg;
      }
      return 0;
    },
    totalFacebookMonthlyAvg() {
      let spend = [];
      for(let i = 0; i < this.reportData.length; i++) {
        if (this.reportData[i].monthly &&
          this.reportData[i].monthly.facebook &&
          this.reportData[i].monthly.facebook.conversions > 0) {
          spend.push(this.getCostPerLead(this.reportData[i].monthly, 'facebook'));
        }
      }
      if(spend.length > 0) {
        let sum = spend.reduce((previous, current) => current += previous);
        let avg = sum / spend.length;
        return avg;
      }
      return 0;
    },
    totalGoogleSpend() {
      let spend = 0;
      for(let i = 0; i < this.reportData.length; i++) {
        spend += this.getSpent(this.reportData[i], 'google');
      }
      return spend;
    },
    totalMicrosoftSpend() {
      let spend = 0;
      for(let i = 0; i < this.reportData.length; i++) {
        spend += this.getSpent(this.reportData[i], 'microsoft');
      }
      return spend;
    },
    totalFacebookSpend() {
      let spend = 0;
      for(let i = 0; i < this.reportData.length; i++) {
        spend += this.getSpent(this.reportData[i], 'facebook');
      }
      return spend;
    },
    totalSpend() {
      return (
        this.totalGoogleSpend +
        this.totalFacebookSpend +
        this.totalMicrosoftSpend
      );
    },
    totalMonthlyAvg() {
      let totalMonthlyLeads = 0;
      for(let i = 0; i < this.reportData.length; i++) {
        if(this.reportData[i].monthly) {
          totalMonthlyLeads += this.getTotalLeads(this.reportData[i].monthly);
        }
      }
      let totalMonthlySpent = 0;
      for(let i = 0; i < this.reportData.length; i++) {
        if(this.reportData[i].monthly) {
          totalMonthlySpent += this.getTotalSpent(this.reportData[i].monthly);
        }
      }
      return totalMonthlySpent / totalMonthlyLeads;
    }

  },
  methods: {
    ...mapActions([
      "setStartDate",
      "setEndDate",
      "setManagement",
      "updateListIds",
      "updateClientIds"
    ]),
    async exportToCSV() {
      let response = await this.$http.post("/lead-tracker/csv", {
        ...this.input,
        ...this.csv
      });
      var csvData = new Blob([response.data], {
        type: "text/csv;charset=utf-8;"
      });
      var link = document.createElement("a");
      link.href = window.URL.createObjectURL(csvData);
      var suffix = this.startDate == this.endDate ? moment(this.startDate).format("YYYY-MM-DD") : moment(this.startDate).format("YYYY-MM-DD") + "_" + moment(this.endDate).format("YYYY-MM-DD");
      if(this.input.management) {
        link.setAttribute("download", "lead-tracking-with-management-fee_"+suffix+".csv");
      } else {
        link.setAttribute("download", "lead-tracking_"+suffix+".csv");
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async getStatusMessage() {
      let response = await this.$http.get("/global-notes/2");
      this.statusMessage = response.data.message;
    },
    onScroll(e) {
      if(document.getElementById('lead-tracking-table')) {
        this.stickyHeader = document.getElementById('lead-tracking-table').getBoundingClientRect().top <= 50;
        if(this.stickyHeader) {
          var offset = (document.getElementById('lead-tracking-table').getBoundingClientRect().top - 50) * -1;
          document.getElementsByClassName('header')[0].style.transform = "translateY(" + offset + "px)";
        } else {
          document.getElementsByClassName('header')[0].style.transform = "translateY(0px)";
        }
      }
    },
    async getClients() {
      let response = await this.$http.get("/clients", {
        params: { paid_media_only: true }
      });
      this.clients = response.data;
    },
    async getLists() {
      let response = await this.$http.get("/lists");
      this.clientLists = response.data;
    },
    formatDate(date) {
      return moment(date).format("YYYY-MM-DD");
    },
    expandAll() {
      this.reportData.map(row => {
        row.showAll = true;
        return row;
      });
    },
    collapseAll() {
      this.reportData.map(row => {
        row.showAll = false;
        return row;
      });
    },
    goToTotal() {
      window.scrollTo(0,document.body.scrollHeight);
    },
    sortColumn() {
      var col = event.toElement.cellIndex;
      var rows = Array.from(document.getElementById('lead-tracking-table').getElementsByTagName('tbody'));
      var totals = rows.pop();
      var _this = this;

      // compare function to toggle row sorting asc and desc
      function compare(a ,b) {
        var one = a.getElementsByTagName('td')[col].innerHTML.toUpperCase();
        var two = b.getElementsByTagName('td')[col].innerHTML.toUpperCase();
        if(col != 0) {
          one = parseInt( one.replace(/\$|,/g, "") );
          two = parseInt( two.replace(/\$|,/g, "") );
        }
        if( !_this.sorted.status || _this.sorted.col !== col ) {
          return one < two ? -1 : one > two ? 1 : 0; // asc
        }
        return one > two ? -1 : one < two ? 1 : 0; // desc
      }
      rows.sort(compare).forEach(tr => document.getElementById('lead-tracking-table').append(tr));
      document.getElementById('lead-tracking-table').append(totals);

      this.sorted.col == col ? this.sorted.status = !this.sorted.status : this.sorted.status = true;
      this.sorted.col = col;

      // Add and remove classes for arrows
      var headerCells = document.getElementById('lead-tracking-table').childNodes[0].getElementsByTagName('th');
      for(var i = 0; i < headerCells.length; i++) { headerCells[i].classList = ''; }
      this.sorted.status ? headerCells[col].classList.add('asc') : headerCells[col].classList.add('desc');
    },

    matchDate() {
      if (this.startDate) {
        this.setEndDate(this.startDate);
      }
      if (this.endDate) {
        this.setStartDate(this.endDate);
      }
    },
    twoDaysAgo() {
      this.setStartDate(
        moment()
          .subtract(2, "days")
          .toString()
      );
      this.setEndDate(
        moment()
          .subtract(2, "days")
          .toString()
      );
    },
    threeDaysAgo() {
      this.setStartDate(
        moment()
          .subtract(3, "days")
          .toString()
      );
      this.setEndDate(
        moment()
          .subtract(3, "days")
          .toString()
      );
    },
    thisWeek() {
      this.setStartDate(
        moment()
          .startOf("week")
          .toString()
      );
      this.setEndDate(
        moment()
          .endOf("week")
          .toString()
      );
    },
    lastWeek() {
      this.setStartDate(
        moment()
          .subtract(1, "week")
          .startOf("week")
          .toString()
      );
      this.setEndDate(
        moment()
          .subtract(1, "week")
          .endOf("week")
          .toString()
      );
    },
    thisMonth() {
      this.setStartDate(
        moment()
          .startOf("month")
          .toString()
      );
      this.setEndDate(
        moment()
          .endOf("month")
          .toString()
      );
    },
    lastMonth() {
      this.setStartDate(
        moment()
          .subtract(1, "month")
          .startOf("month")
          .toString()
      );
      this.setEndDate(
        moment()
          .subtract(1, "month")
          .endOf("month")
          .toString()
      );
    },
    twoMonthsAgo() {
      this.setStartDate(
        moment()
          .subtract(2, "months")
          .startOf("month")
          .toString()
      );
      this.setEndDate(
        moment()
          .subtract(2, "months")
          .endOf("month")
          .toString()
      );
    },
    threeMonthsAgo() {
      this.setStartDate(
        moment()
          .subtract(3, "months")
          .startOf("month")
          .toString()
      );
      this.setEndDate(
        moment()
          .subtract(3, "months")
          .endOf("month")
          .toString()
      );
    },
    thisYear() {
      this.setStartDate(
        moment()
          .startOf("year")
          .toString()
      );
      this.setEndDate(
        moment()
          .endOf("year")
          .toString()
      );
    },
    lastYear() {
      this.setStartDate(
        moment()
          .subtract(1, "year")
          .startOf("year")
          .toString()
      );
      this.setEndDate(
        moment()
          .subtract(1, "year")
          .endOf("year")
          .toString()
      );
    },
    async updateData() {
      this.status.loading = true;
      try {
        this.noReportData = false;
        let response = await this.$http.post("/lead-tracker", this.input);
        this.reportData = response.data.map(row => {
          row.showAll = false;
          return row;
        });
      } catch (e) {
        console.log(e.response.data.errors);
        this.reportData = [];
        this.noReportData = true;
      }
      this.status.loading = false;
    },
    getFormLeads(data) {
      let count = 0;
      if (data.google) {
        count += parseInt(data.google.conversions);
      }
      if (data.microsoft) {
        count += parseInt(data.microsoft.conversions);
      }
      if (data.facebook) {
        count += parseInt(data.facebook.conversions);
      }
      return count;
    },
    getTotalLeads(data) {
      let count = 0;
      count += this.getFormLeads(data);

      if (data.click_to_call) {
        count += parseInt(data.click_to_call.conversions);
      }
      return count;
    },
    getTotalSpent(data) {
      let spend = 0;
      if (data.google) {
        spend += data.google.spent;
      }
      if (data.microsoft) {
        spend += data.microsoft.spent;
      }
      if (data.facebook) {
        spend += data.facebook.spent;
      }
      if (this.input.management && data.fees && data.fees.management) {
        spend += data.fees.management;
      }
      if (this.input.management && data.fees && data.fees.call_tracking) {
        spend += data.fees.call_tracking;
      }
      return spend;
    },
    getTotalCostPerLead(data) {
      if (this.getTotalLeads(data) == 0) {
        return 0;
      } else {
        return this.getTotalSpent(data) / this.getTotalLeads(data);
      }
    },
    getSpent(data, channel) {
      let spend = data[channel] ? data[channel].spent : 0;
      if (this.input.management && data.fees) {
        let totalSpend = 0;
        let totalFees = (data.fees.management ? data.fees.management : 0) + (data.fees.call_tracking ? data.fees.call_tracking : 0);
        if (data.google) {
          totalSpend += data.google.spent;
        }
        if (data.microsoft) {
          totalSpend += data.microsoft.spent;
        }
        if (data.facebook) {
          totalSpend += data.facebook.spent;
        }
        let percent = spend / totalSpend;
        if(totalSpend == 0) { percent = 1; }
        spend += totalFees * percent;
      }
      return spend;
    },
    getCostPerLead(data, channel) {
      if (data[channel].conversions > 0) {
        return (
          this.getSpent(data, channel) /
          (parseInt(data[channel].conversions) + data[channel].calls)
        );
      } else {
        return 0;
      }
    },
    getTotalChannelCostPerLead(spend, leads) {
      if (leads > 0) {
        return spend / leads;
      } else {
        return 0;
      }
    }
  }
};
</script>

<style lang="scss">
#lead-tracker {
  .status-message {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
    margin-bottom: 15px;
    background: lighten($blue, 50%);
    border-radius: 4px;
    flex-direction: row;
    .message {
      flex: 1;
    }
    a {
      display: block;
      text-transform: uppercase;
      font-weight: 700;
    }
  }
  thead {
    th {
      white-space: nowrap;
      position: relative;
      &.asc:after, &.desc:after {
        content: '';
        position: absolute;
        border-style: solid;
        border-width: 5px 4px;
        border-color: transparent transparent #555 transparent;
        right: -2px;
        top: 12px;
      }
      &.desc:after {
        border-color: #555 transparent transparent transparent;
        top: 18px;
      }
    }
  }
  fieldset {
    display: block;
    label {
      display: inline-block;
      margin: 0 5px;
    }
  }

  #settings {
    background: lighten($light-gray, 3%);
    padding: 15px 30px;
    margin-bottom: 30px;
  }

  #results {
    margin-top: 60px;
  }

  .button-row {
    & + .form-control {
      margin-top: 15px;
    }
  }
  .button.match {
    margin-top: 20px;
  }

  .client-selection {
    padding: 5px;
    height: 225px;
    overflow-y: scroll;
    border: 1px solid $medium-gray;
    border-radius: 5px;
    background: $white;
  }

  .client-list-edit {
    display: inline-block;
    margin-left: 5px;
    font-size: 12px;
  }

  .no-data-message {
    padding: 30px;
    text-align: center;
  }

  .controls {
    font-size: 12px;
    margin: 15px 10px;
  }

  thead {
    &.header {
      background: #fff;
    }
    th {
      font-size: 12px;
      text-align: right;
      cursor: pointer;

      &:first-of-type {
        text-align: left;
      }
    }
  }

  tbody {
    td {
      padding: 6px 15px;
      text-align: right;

      &:first-of-type {
        text-align: left;
      }

      &.medium {
        text-align: right;
        font-size: 12px;
        padding: 6px 15px;
      }
    }

    tr {
      background: $white;

      &.breakout {
        background: #fafafa;
      }
    }
  }
  a.btn.btn--goto {
    border: 1px solid $blue;
    border-radius: 3px;
    padding: 2px 8px;
    color: #fff;
    background: $blue;
    font-weight: 900;
    margin-right: 20px;
    transition: all .2s;
    &:hover {
      color: $blue;
      background: transparent;
    }
  }

  .align-center {
    text-align: center;
  }
}
</style>
